import React, {useEffect, useState} from "react";
import {CloseOutlined, LeftOutlined, RightOutlined} from "@ant-design/icons";
import {useNavigate, useParams} from "react-router-dom";
import SharedService from "../../services/shared/service";
import {Tooltip} from "antd";
import {useAuth} from "oidc-react";
import {useJwt} from "react-jwt";
import {formatDate} from "../../utils/datetime.utils";

const HeaderField = ({label, value, tooltip, className = ""}) => (
    <div
        className={`flex flex-col justify-center px-3 border-r border-white/25 min-w-0 flex-1 ${className}`}
    >
        <div className="text-white/50 text-[11px] font-amina uppercase tracking-wide truncate">{label}</div>
        <Tooltip overlayStyle={{maxWidth: "320px"}} title={tooltip ?? value}>
            <div className="text-white text-[13px] font-amina mt-0.5 truncate">{value}</div>
        </Tooltip>
    </div>
);

const InvoiceHeader = ({
    documentId,
    invoiceData,
    tableItemIndex,
    currentItemIndex,
    setCurrentItemIndex,
    listPagination,
    setlistPagination,
    setinvoiceData,
    selectedItem,
    invoiceDetailData,
    selectedRadioOption,
}) => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {userData} = useAuth();
    const token = userData?.access_token;
    useJwt(token);
    const [itemIndex, setItemIndex] = useState(null);
    const [invoiceIdData, setInvoiceIdData] = useState("");

    useEffect(() => {
        if (tableItemIndex || tableItemIndex === 0) setItemIndex(tableItemIndex);
    }, [tableItemIndex]);

    useEffect(() => {
        const findData = (key) =>
            invoiceDetailData.find((item) => item.key === key)?.correctedValue ??
            invoiceDetailData.find((item) => item.key === key)?.value;
        setInvoiceIdData(findData("InvoiceId"));
    }, [invoiceDetailData]);

    useEffect(() => {
        const nextId = invoiceData[itemIndex]?.id;
        if (nextId !== undefined) navigate(`/details/${nextId}`);
    }, [itemIndex]);

    const fetchInvoiceSearchData = async (next) => {
        try {
            const data =
                selectedRadioOption === "latestInvoice"
                    ? {}
                    : selectedRadioOption === "toBeProcessed"
                    ? {status: ["AwaitingReview", "OCRProcessed"]}
                    : {status: [selectedRadioOption]};

            const params = {
                pageNumber: next ? listPagination.pageNumber + 1 : listPagination.pageNumber - 1,
                pageSize: listPagination.pageSize,
                orderBy: "createdOn:Desc",
            };

            const response = await SharedService.post({
                token,
                url: "documents/search",
                data,
                params,
            });
            setinvoiceData(response.items);
            listPagination.pageNumber = params.pageNumber;
            setlistPagination({...listPagination, totalItemCount: response.totalItemCount});
            return true;
        } catch {
            return false;
        }
    };

    const handleClose = () => navigate("/invoice");

    const handleNext = async () => {
        try {
            const nextIndex = itemIndex + 1;
            if (nextIndex === 10) {
                const success = await fetchInvoiceSearchData(true);
                if (success) setItemIndex(0);
            } else {
                setItemIndex(nextIndex);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handlePrevious = async () => {
        try {
            const prevIndex = itemIndex - 1;
            if (prevIndex === -1) {
                const success = await fetchInvoiceSearchData(false);
                if (success) setItemIndex(9);
            } else {
                setItemIndex(prevIndex);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const isPreviousDisabled = itemIndex === 0 && listPagination.pageNumber === 1;
    const totalPages = Math.ceil(listPagination.totalItemCount / listPagination.pageSize);
    const isNextDisabled = currentItemIndex === invoiceData.length - 1 && listPagination.pageNumber === totalPages;

    const invoiceLabel =
        selectedItem?.isUploadedToShipsure === true || selectedItem?.processingStatus === "InvoiceCreated"
            ? `${invoiceIdData}, Uploaded: ${formatDate(
                  selectedItem?.shipsureUploadDateTime ?? selectedItem?.updatedOn,
                  "dd MMM yyyy, hh:mm tt"
              )}`
            : null;

    return (
        <div
            className="rounded-[12px] overflow-hidden mx-2 sm:mx-4 mt-4 mb-3"
            style={{backgroundColor: "var(--primary-color)"}}
        >
            <div className="flex items-stretch min-h-14 py-2">
                {/* Fields */}
                <div className="flex flex-1 min-w-0 items-stretch">
                    <HeaderField label="Document ID" value={id}/>
                    <HeaderField
                        label="Reference ID"
                        value={selectedItem?.referenceId}
                        className="hidden sm:flex"
                    />
                    {invoiceLabel && (
                        <HeaderField
                            label="Invoice"
                            value={invoiceLabel}
                            className="hidden md:flex"
                        />
                    )}
                    {selectedItem?.shipsureVoucher && (
                        <HeaderField
                            label="Voucher"
                            value={selectedItem.shipsureVoucher}
                            className="hidden md:flex"
                        />
                    )}
                    <HeaderField label="Invoice Status" value={selectedItem?.processingStatus}/>
                    <HeaderField
                        label="Last Updated By"
                        value={selectedItem?.updatedBy}
                        className="hidden lg:flex"
                    />
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 px-2 sm:px-4 border-l border-white/15 flex-shrink-0">
                    <Tooltip title="Previous">
                        <button
                            onClick={handlePrevious}
                            disabled={isPreviousDisabled}
                            className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-[7px] text-[13px] font-normal transition-opacity
                                ${isPreviousDisabled ? "text-white/30 cursor-not-allowed" : "text-white hover:bg-white/10 cursor-pointer"}`}
                            aria-label="Previous"
                        >
                            <LeftOutlined className="text-[11px]"/>
                            <span className="hidden sm:inline">Prev</span>
                        </button>
                    </Tooltip>
                    <Tooltip title="Next">
                        <button
                            onClick={handleNext}
                            disabled={isNextDisabled}
                            className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-[7px] text-[13px] font-normal transition-opacity
                                ${isNextDisabled ? "text-white/30 cursor-not-allowed" : "text-white hover:bg-white/10 cursor-pointer"}`}
                            aria-label="Next"
                        >
                            <span className="hidden sm:inline">Next</span>
                            <RightOutlined className="text-[11px]"/>
                        </button>
                    </Tooltip>
                    <Tooltip title="Close">
                        <button
                            onClick={handleClose}
                            className="flex items-center justify-center w-8 h-8 rounded-[7px] text-white hover:bg-white/10 cursor-pointer ml-1"
                            aria-label="Close"
                        >
                            <CloseOutlined className="text-[15px]"/>
                        </button>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

export default InvoiceHeader;
